import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import { completeOnboarding } from '../api/endpoints';
import { BookOpen, Brain, BarChart3, Flame, ChevronRight, Sparkles, GraduationCap } from 'lucide-react';

const OnboardingPage = () => {
  const { tenant } = useTenant();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  useEffect(() => {
    document.title = `Welcome | ${tenant?.name || 'MedMCQ'}`;
  }, [tenant]);

  // If user already completed onboarding, redirect to home
  useEffect(() => {
    if (user?.onboarding_completed) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  const handleComplete = async () => {
    try {
      await completeOnboarding();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error completing onboarding:', error);
      navigate('/', { replace: true });
    }
  };

  const handleSkip = async () => {
    await handleComplete();
  };

  const steps = [
    // Step 0: Welcome
    {
      content: (
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-cyan-400 to-teal-500 flex items-center justify-center shadow-lg">
            <GraduationCap className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-3">
            Welcome to {tenant?.name || 'MedMCQ'}!
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-300 mb-2">
            Hey {user?.full_name?.split(' ')[0] || 'there'}, great to have you on board.
          </p>
          <p className="text-slate-500 dark:text-slate-400">
            Let's quickly show you around so you get the most out of your study time.
          </p>
        </div>
      ),
    },
    // Step 1: Diagnostic Tests
    {
      content: (
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-gradient-to-br from-purple-400 to-indigo-500 flex items-center justify-center shadow-lg">
            <Brain className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">
            Your Personal AI Tutor
          </h2>
          <p className="text-slate-600 dark:text-slate-300 mb-4">
            Take 3 short diagnostic tests and we'll build you a personalised study plan.
            The AI learns your strengths and weak spots, then picks the perfect questions for you.
          </p>
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 border border-purple-100 dark:border-purple-800 text-left">
            <div className="flex items-start gap-3 mb-2">
              <Sparkles className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-purple-700 dark:text-purple-300">
                <strong>110 questions per test</strong> — covers all your key topics.
                After all 3 tests, your AI-Guided study mode activates automatically.
              </p>
            </div>
          </div>
        </div>
      ),
    },
    // Step 2: Features Tour
    {
      content: (
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-5">
            What You Can Do
          </h2>
          <div className="grid grid-cols-1 gap-3 text-left">
            <div className="flex items-start gap-3 bg-cyan-50 dark:bg-cyan-900/20 rounded-xl p-3 border border-cyan-100 dark:border-cyan-800">
              <BookOpen className="w-5 h-5 text-cyan-600 dark:text-cyan-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">Practice Questions</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Filter by topic, difficulty, and question type</p>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-3 border border-emerald-100 dark:border-emerald-800">
              <BarChart3 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">Track Your Progress</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Analytics show your improvement over time</p>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3 border border-amber-100 dark:border-amber-800">
              <Flame className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">Study Streaks</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Build a daily habit and watch your streak grow</p>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-purple-50 dark:bg-purple-900/20 rounded-xl p-3 border border-purple-100 dark:border-purple-800">
              <Brain className="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">Spaced Repetition</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Questions you get wrong come back at the perfect time</p>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    // Step 3: Get Started
    {
      content: (
        <div className="text-center">
          <div className="text-5xl mb-4">🚀</div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">
            You're All Set!
          </h2>
          <p className="text-slate-600 dark:text-slate-300 mb-6">
            We recommend starting with your first diagnostic test to unlock AI-powered study guidance.
            Or jump straight into practice questions — it's up to you!
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => { handleComplete().then(() => navigate('/diagnostic')); }}
              className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-bold py-3 px-6 rounded-xl shadow-md hover:shadow-lg transition-all text-sm"
            >
              Start Diagnostic Test
            </button>
            <button
              onClick={() => { handleComplete().then(() => navigate('/questions')); }}
              className="w-full bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 font-semibold py-3 px-6 rounded-xl border-2 border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500 transition-all text-sm"
            >
              Jump to Questions
            </button>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-cyan-50 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-md w-full p-8 relative overflow-hidden">
        {/* Progress bar */}
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-slate-100 dark:bg-slate-700">
          <div
            className="h-full bg-gradient-to-r from-cyan-400 to-teal-500 transition-all duration-500 ease-out"
            style={{ width: `${((step + 1) / steps.length) * 100}%` }}
          />
        </div>

        {/* Step counter */}
        <div className="flex items-center justify-between mb-6 mt-2">
          <span className="text-xs text-slate-400 font-medium">
            {step + 1} of {steps.length}
          </span>
          {step < steps.length - 1 && (
            <button
              onClick={handleSkip}
              className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 font-medium transition-colors"
            >
              Skip
            </button>
          )}
        </div>

        {/* Content */}
        <div className="mb-8">
          {steps[step].content}
        </div>

        {/* Navigation */}
        {step < steps.length - 1 && (
          <button
            onClick={() => setStep(s => s + 1)}
            className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-bold py-3 px-6 rounded-xl shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 text-sm"
          >
            Continue
            <ChevronRight className="w-4 h-4" />
          </button>
        )}

        {/* Step dots */}
        <div className="flex justify-center gap-2 mt-6">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full transition-all ${
                i === step
                  ? 'bg-cyan-500 w-6'
                  : i < step
                  ? 'bg-cyan-300'
                  : 'bg-slate-200 dark:bg-slate-600'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
